import classNames from "classnames";
import React, { useCallback, useMemo } from "react";

import { Pagination, PaginationProps } from "../pagination/pagination";

import { CSS_CLASSES } from "./table-css-classes";

export type PaginationOptions = Partial<
  Omit<PaginationProps, "itemsPerPage" | "onItemsPerPageChange" | "totalItems" | "page" | "onViewAllClick"> & {
    onItemsPerPageChange: (pageSize: number) => void;
  }
>;

type TablePaginationProps = {
  paginationOptions: PaginationOptions;
  gotoPage: (updater: number | ((pageIndex: number) => number)) => void;
  setPageSize: (pageSize: number) => void;

  pageSize: number;
  totalItems: number;
  pageIndex: number;
};

export const TablePagination: React.FunctionComponent<TablePaginationProps> = ({
  paginationOptions: { onPageChange, onItemsPerPageChange, ...restOptions },
  gotoPage,
  pageSize,
  setPageSize,
  totalItems,
  pageIndex,
}) => {
  const onPageChangeHandler = useCallback(
    (page) => {
      gotoPage(page - 1);
      onPageChange && onPageChange(page - 1);
    },
    [gotoPage, onPageChange]
  );

  const onItemsPerPageChangeHandler = useCallback(
    ({ value: newPageSize }) => {
      const pageSize = +newPageSize === 0 ? totalItems : +newPageSize;
      setPageSize(pageSize);
      onItemsPerPageChange && onItemsPerPageChange(pageSize);
    },
    [setPageSize, onItemsPerPageChange, totalItems]
  );

  const onViewAllClickHandler = useCallback(() => {
    setPageSize(totalItems);
    onItemsPerPageChange && onItemsPerPageChange(totalItems);
  }, [setPageSize, totalItems, onItemsPerPageChange]);

  const paginationClasses = useMemo(
    () => classNames(CSS_CLASSES.TABLE_PAGINATION, restOptions.className),
    [restOptions.className]
  );

  return (
    <Pagination
      onPageChange={onPageChangeHandler}
      onItemsPerPageChange={onItemsPerPageChangeHandler}
      totalItems={totalItems}
      itemsPerPage={pageSize}
      page={pageIndex + 1}
      onViewAllClick={onViewAllClickHandler}
      {...restOptions}
      className={paginationClasses}
    />
  );
};
